import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { Button, Modal, Popover } from 'antd';
import DownOutlined from '@ant-design/icons/DownOutlined';
import { useTranslation } from 'react-i18next';
import { useLlmConfig, type LlmConfig } from '@/api/llmConfig';
import type { RunMode } from '@/api/runs';
import { MODE_ICONS, MODE_KEYS } from '../conversation/ModeBadge';
import { formatLimitMb } from './quota';
import styles from './composer.module.css';

/** The kit's mode icon, on the button and each option. */
const ModeIcon = ({ mode }: { mode: RunMode }) => {
  const Icon = MODE_ICONS[mode];
  return <Icon aria-hidden className={styles.modeIcon} />;
};

/**
 * The mode popover (spec §4): exactly one mode. Arrow keys move the
 * candidate only; a click (or tap), Enter or Apply commits it, on every
 * layout; Escape (or a press outside) closes and keeps the previous mode.
 * Below 640 px it is a dialog; Apply stays there for keyboard users who
 * move the candidate with the arrows (D-28: the prompt-14 phone behaviour,
 * where a tap only moved the candidate, contradicted §4).
 */

export const MODES: RunMode[] = [
  'standard',
  'thinking',
  'comparison',
  'chat_only',
];

const DESCRIPTION_KEYS: Record<RunMode, string> = {
  standard: 'v2.composer.modeDesc_normal',
  thinking: 'v2.composer.modeDesc_deep',
  comparison: 'v2.composer.modeDesc_compare',
  chat_only: 'v2.composer.modeDesc_chat',
};

/** The use-case and limit guidance, with the server's numbers. */
export const ModeGuidance = ({ config }: { config: LlmConfig }) => {
  const { t } = useTranslation();
  const { standard, thinking, comparison } = config.modes;
  return (
    <details className={styles.guidance} data-testid="mode-guidance">
      <summary>{t('v2.composer.compareDetails')}</summary>
      <ul>
        <li>
          {t('v2.composer.limitsStandard', {
            top: standard.top_files,
            passages: standard.passages,
          })}
        </li>
        <li>
          {t('v2.composer.limitsDeep', {
            top: thinking.top_files,
            mib: thinking.max_total_size_mib,
          })}
        </li>
        <li>
          {t('v2.composer.limitsCompare', {
            min: comparison.min_files,
            maxFiles: comparison.max_files,
            maxMb: formatLimitMb(
              comparison.max_total_bytes,
              config.bytes_per_mb,
            ),
          })}
        </li>
        <li>{t('v2.composer.limitsChat')}</li>
      </ul>
      <p className={styles.guidanceNote}>{t('v2.composer.modeUseCases')}</p>
    </details>
  );
};

interface ModePickerProps {
  mode: RunMode;
  onChange: (mode: RunMode) => void;
  disabled?: boolean;
  /** Below 640 px: a dialog instead of a popover. */
  compact: boolean;
}

const ModePicker = ({ mode, onChange, disabled, compact }: ModePickerProps) => {
  const { t } = useTranslation();
  const config = useLlmConfig();
  const [open, setOpen] = useState(false);
  const [candidate, setCandidate] = useState<RunMode>(mode);
  const trigger = useRef<HTMLButtonElement>(null);
  const group = useRef<HTMLDivElement>(null);
  const id = useId();

  const focusChecked = () =>
    group.current
      ?.querySelector<HTMLInputElement>('input[type="radio"]:checked')
      ?.focus();

  // The popover's content is there on the next frame. The dialog form
  // focuses in afterOpenChange instead: its content mounts later (D-23).
  useEffect(() => {
    if (!open || compact) return;
    const frame = requestAnimationFrame(focusChecked);
    return () => cancelAnimationFrame(frame);
  }, [open, compact]);

  const show = () => {
    setCandidate(mode);
    setOpen(true);
  };

  const close = (returnFocus = true) => {
    setOpen(false);
    if (returnFocus) requestAnimationFrame(() => trigger.current?.focus());
  };

  const commit = (value: RunMode) => {
    if (value !== mode) onChange(value);
    close();
  };

  /** Enter in the radio list commits the candidate. */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(candidate);
    }
  };

  /** Escape anywhere in the popover keeps the previous mode. */
  const onPanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    setCandidate(mode);
    close();
  };

  const radios = (
    <div
      ref={group}
      role="radiogroup"
      aria-labelledby={`${id}-title`}
      className={styles.modeList}
      onKeyDown={onKeyDown}
      data-testid="mode-options"
    >
      {MODES.map(value => (
        <label
          key={value}
          className={styles.modeOption}
          data-candidate={candidate === value || undefined}
          onClick={(event: MouseEvent) => {
            // A real click or tap commits; keyboard "clicks" (detail 0)
            // only move the candidate.
            if (event.detail > 0) {
              event.preventDefault();
              commit(value);
            }
          }}
        >
          <input
            type="radio"
            name={`${id}-mode`}
            value={value}
            checked={candidate === value}
            onChange={() => setCandidate(value)}
            data-testid={`mode-option-${value}`}
          />
          <span className={styles.modeText}>
            <span className={styles.modeName}>
              <ModeIcon mode={value} />
              {t(MODE_KEYS[value])}
              {value === mode ? (
                <span className={styles.modeCurrent}>
                  {t('v2.composer.modeCurrent')}
                </span>
              ) : null}
            </span>
            <span className={styles.modeDescription}>
              {t(DESCRIPTION_KEYS[value])}
            </span>
          </span>
        </label>
      ))}
    </div>
  );

  const footer = (
    <div className={styles.modeFooter}>
      <Button
        onClick={() => {
          setCandidate(mode);
          close();
        }}
      >
        {t('v2.common.cancel')}
      </Button>
      <Button
        type="primary"
        onClick={() => commit(candidate)}
        data-testid="mode-apply"
      >
        {t('v2.common.apply')}
      </Button>
    </div>
  );

  const button = (
    <button
      ref={trigger}
      type="button"
      className={styles.modeButton}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={t('v2.composer.modeBtn', { m: t(MODE_KEYS[mode]) })}
      disabled={disabled}
      onClick={() => (open ? close() : show())}
      data-testid="mode-button"
      data-mode={mode}
    >
      <ModeIcon mode={mode} />
      <span>{t(MODE_KEYS[mode])}</span>
      <DownOutlined aria-hidden className={styles.modeChevron} />
    </button>
  );

  if (compact) {
    return (
      <>
        {button}
        <Modal
          open={open}
          title={<span id={`${id}-title`}>{t('v2.composer.modeTitle')}</span>}
          onCancel={() => {
            setCandidate(mode);
            close();
          }}
          footer={footer}
          destroyOnClose
          afterOpenChange={visible => {
            if (visible) focusChecked();
          }}
        >
          <p className={styles.modeNote}>{t('v2.composer.modeNote')}</p>
          {radios}
          <ModeGuidance config={config} />
        </Modal>
      </>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={next => {
        // A press outside: keep the previous mode.
        if (!next) {
          setCandidate(mode);
          close(false);
        }
      }}
      trigger="click"
      placement="topLeft"
      // Shift into the viewport instead of flipping to topRight, which put
      // the 444 px panel past the left edge at 640 px (D-11). The root is
      // transparent; its inline padding keeps the visible box 8 px inside.
      // It also shifts down when the centred composer of an empty Q&A
      // leaves too little room above (prompt 24).
      align={{
        overflow: { adjustX: false, shiftX: true, adjustY: true, shiftY: true },
      }}
      overlayStyle={{ paddingInline: 8, maxWidth: '100vw' }}
      destroyTooltipOnHide
      content={
        <div
          className={styles.modePanel}
          onKeyDown={onPanelKeyDown}
          role="dialog"
          aria-labelledby={`${id}-title`}
          data-testid="mode-popover"
        >
          <div className={styles.modeHead}>
            <strong id={`${id}-title`}>{t('v2.composer.modeTitle')}</strong>
            <span className={styles.modeNote}>{t('v2.composer.modeNote')}</span>
          </div>
          <div className={styles.modeScroll}>
            {radios}
            <ModeGuidance config={config} />
          </div>
          {footer}
        </div>
      }
    >
      {button}
    </Popover>
  );
};

export default ModePicker;
